import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormikProps } from 'formik';
import { usePanel } from 'hooks/usePanel';
import { GenericLoading } from 'components/ui/Loader';
import GenericTabs from 'components/navigation/Tabs/Tabs';
import { GenericDocumentUploader } from 'components/domain/Document/GenericDocumentUploader';
import { IdentityDocumentUploader } from 'components/domain/IdentityUpload';
import { ActorForm } from './ActorForm';
import { ActorType } from '../enums/actorType';
import { ActorLayoutVariant } from '../enums/actorLayoutVariant';
import { useEnhancedFormConfig } from '../hooks/useEnhancedFormConfig';
import { useSubmitActorForm, ActorFormValues } from '../hooks/optim/useSubmitActorForm';
import { useFetchPersonWithDocuments } from '../hooks/useFetchPersonWithDocuments';
import { legalEntityFormConfig } from '../form/optim/legalEntityFormConfig';
import {
  DocumentVerificationStatusEnum,
  FileTypeEnum,
  LegalEntityDto,
  NaturalPersonDto
} from 'api/actor';
import { FileTypeEnum as FilesFileTypeEnum } from 'api/files';
import { FileTypeEnum as DocumentFileTypeEnum } from 'api/documents';
import { ReferentialKey } from 'api/referential';
import { ContextEnum } from 'api/files';
import { filesAPI } from 'services/swagger-api';
import { selectReferentialDataByKey } from 'features/referentials/selectors/selectReferentialDataByKey';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { NsKey } from 'enums/NsKey';
import { useSelector } from 'react-redux';

export interface PersonWithDocumentsProps {
  personId: string;
  onSuccess?: () => void;
  onDocumentChange?: () => Promise<void> | void;
}

interface DocumentConfig {
  name: string;
  fileType: FileTypeEnum;
  storedFileId?: string;
  /** Identifier used when uploading (defaults to the wrapper personId if absent). */
  personneId?: string;
  /** Extra context required to trigger identity verification on PieceIdentite. */
  identity?: {
    naturalPersonId: string;
    personDisplayName?: string;
    verificationStatus?: DocumentVerificationStatusEnum;
  };
}

const toFilesFileType = (ft: FileTypeEnum): FilesFileTypeEnum => ft as unknown as FilesFileTypeEnum;
const toDocumentsFileType = (ft: FileTypeEnum): DocumentFileTypeEnum => ft as unknown as DocumentFileTypeEnum;

const formatPersonName = (
  firstName?: string | null,
  lastName?: string | null
): string | undefined => {
  const parts = [firstName, lastName].filter((p): p is string => !!p && p.trim().length > 0);
  return parts.length > 0 ? parts.join(' ') : undefined;
};

const isDocumentCompleted = (doc: DocumentConfig): boolean => {
  if (!doc.storedFileId) return false;
  if (doc.fileType === FileTypeEnum.PieceIdentite) {
    return doc.identity?.verificationStatus === DocumentVerificationStatusEnum.Verified;
  }
  return true;
};

export const PersonWithDocuments: React.FC<PersonWithDocumentsProps> = ({
  personId,
  onSuccess,
  onDocumentChange
}) => {
  const formikRef = useRef<FormikProps<ActorFormValues>>(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [activeTab, setActiveTab] = useState<number | string>(1);
  const { setPanelActions, closePanel } = usePanel();
  const { t } = useDynamicTranslation([NsKey.Common]);

  const { person, personType, documents, memberDocuments, isLoading, refetch } =
    useFetchPersonWithDocuments(personId);

  const companyRoleReferentials = useSelector(
    selectReferentialDataByKey(ReferentialKey.CompanyRole)
  );
  const gerantRoleId = useMemo(
    () => companyRoleReferentials?.find(r => r.code === 'GERANT')?.id,
    [companyRoleReferentials]
  );

  const { naturalPersonFormConfig } = useEnhancedFormConfig();

  const formConfig = useMemo(
    () => ({
      [ActorType.Natural]: naturalPersonFormConfig,
      [ActorType.Legal]: legalEntityFormConfig
    }),
    [naturalPersonFormConfig]
  );

  const { editConfig } = useSubmitActorForm({
    person: person ?? undefined,
    postSubmit: () => {
      onSuccess?.();
      closePanel();
    },
    formikRef
  });

  useEffect(() => {
    if (activeTab === 1) {
      setPanelActions({
        closeLabel: 'COMMON.CANCEL',
        submitLabel: 'COMMON.VALIDATE',
        submitDisabled: !isFormValid,
        onSubmit: () => formikRef.current?.submitForm()
      });
    } else {
      setPanelActions({
        closeLabel: 'COMMON.CANCEL',
        submitLabel: 'COMMON.VALIDATE',
        onSubmit: () => {
          onSuccess?.();
          closePanel();
        }
      });
    }
  }, [isFormValid, activeTab, setPanelActions, closePanel, onSuccess]);

  const handleValidationChange = useCallback((isValid: boolean) => {
    setIsFormValid(isValid);
  }, []);

  const refreshAfterDocumentChange = useCallback(async () => {
    await refetch();
    await onDocumentChange?.();
  }, [refetch, onDocumentChange]);

  const handleDocumentUpload = useCallback(
    async (file: File, fileType: FileTypeEnum, targetPersonneId?: string) => {
      try {
        const response = await filesAPI.uploadFile({
          fileType: toFilesFileType(fileType),
          context: ContextEnum.Person,
          objetId: targetPersonneId || personId,
          file
        });
        if (response.isSuccess) {
          await refreshAfterDocumentChange();
          return { success: true };
        }
        return { success: false, error: response.errorMessage || 'Upload failed' };
      } catch (error) {
        return { success: false, error: 'Upload failed' };
      }
    },
    [personId, refreshAfterDocumentChange]
  );

  const handleDocumentRemove = useCallback(
    async (storedFileId: string) => {
      await filesAPI.deleteFileByStoredFileId({ storedFileId });
      await refreshAfterDocumentChange();
    },
    [refreshAfterDocumentChange]
  );

  const gerants = useMemo((): NaturalPersonDto[] => {
    if (personType !== 'morale' || !gerantRoleId || !person) return [];
    const legalEntity = person as LegalEntityDto;
    return legalEntity.naturalPersons?.filter(np => np.companyRole === gerantRoleId) ?? [];
  }, [person, personType, gerantRoleId]);

  const documentsConfig = useMemo((): DocumentConfig[] => {
    if (personType === 'physique' && person) {
      const naturalPerson = person as NaturalPersonDto;
      const displayName = formatPersonName(naturalPerson.firstName, naturalPerson.lastName);
      // Le RIB n'est pas demandé ici : il est collecté à l'étape mandat SEPA
      // (carte Conditions), et le statut "assuré complet" côté back ne vérifie
      // que la pièce d'identité.
      return [
        {
          name: t('DOCUMENT_TYPE.ID_CARD'),
          fileType: FileTypeEnum.PieceIdentite,
          storedFileId: documents.cni?.storedFileId,
          identity: {
            naturalPersonId: personId,
            personDisplayName: displayName,
            verificationStatus: documents.cni?.verificationStatus
          }
        }
      ];
    }

    const configs: DocumentConfig[] = [
      {
        name: t('DOCUMENT_TYPE.KBIS'),
        fileType: FileTypeEnum.Kbis,
        storedFileId: documents.kbis?.storedFileId
      }
    ];

    for (const gerant of gerants) {
      if (!gerant.id) continue;
      const gerantCni = (memberDocuments[gerant.id] ?? []).find(
        d => d.fileType === FileTypeEnum.PieceIdentite
      );
      const displayName = formatPersonName(gerant.firstName, gerant.lastName);
      configs.push({
        name: displayName
          ? `${t('DOCUMENT_TYPE.ID_CARD')} - ${displayName}`
          : t('DOCUMENT_TYPE.ID_CARD'),
        fileType: FileTypeEnum.PieceIdentite,
        storedFileId: gerantCni?.storedFileId,
        personneId: gerant.id,
        identity: {
          naturalPersonId: gerant.id,
          personDisplayName: displayName,
          verificationStatus: gerantCni?.verificationStatus
        }
      });
    }

    return configs;
  }, [personType, person, personId, documents, gerants, memberDocuments, t]);

  const missingDocumentsCount = useMemo(
    () => documentsConfig.filter(doc => !isDocumentCompleted(doc)).length,
    [documentsConfig]
  );

  const renderDocument = useCallback(
    (doc: DocumentConfig) => {
      const key = `${doc.fileType}-${doc.personneId ?? 'main'}`;

      if (doc.fileType === FileTypeEnum.PieceIdentite && doc.identity) {
        return (
          <IdentityDocumentUploader
            key={key}
            documentName={doc.name}
            naturalPersonId={doc.identity.naturalPersonId}
            personDisplayName={doc.identity.personDisplayName}
            storedFileId={doc.storedFileId}
            verificationStatus={doc.identity.verificationStatus}
            onSuccess={refreshAfterDocumentChange}
            onRemove={handleDocumentRemove}
          />
        );
      }

      return (
        <GenericDocumentUploader
          key={key}
          documentName={doc.name}
          fileType={toDocumentsFileType(doc.fileType)}
          storedFileId={doc.storedFileId}
          onUpload={file => handleDocumentUpload(file, doc.fileType, doc.personneId)}
          onRemove={handleDocumentRemove}
        />
      );
    },
    [handleDocumentUpload, handleDocumentRemove, refreshAfterDocumentChange]
  );

  if (isLoading || !person) {
    return <GenericLoading loading isParentSized />;
  }

  return (
    <GenericTabs defaultTab={activeTab} onChange={setActiveTab}>
      <GenericTabs.Item label={t('COMMON.LABELS.INFORMATIONS')} value={1} />
      <GenericTabs.Item
        label={t('COMMON.LABELS.DOCUMENTS')}
        value={2}
        notification={missingDocumentsCount || undefined}
      />
      <GenericTabs.Content value={1}>
        {editConfig && (
          <ActorForm<ActorFormValues>
            layoutVariant={ActorLayoutVariant.ActorWithDefaultDocumentsActor}
            formConfig={formConfig}
            formikRef={formikRef}
            editConfig={editConfig}
            onValidationChange={handleValidationChange}
          />
        )}
      </GenericTabs.Content>
      <GenericTabs.Content value={2}>
        <div className="p-4 space-y-4">{documentsConfig.map(renderDocument)}</div>
      </GenericTabs.Content>
    </GenericTabs>
  );
};
